// SPDX-License-Identifier: GPL-3.0-or-later
// How-to-play text of the game SigmaShake Pinball, table SIGMABOTS (THEME.md section 9.4).
// The site card, the overlay panel (long form) and the strip and card rows (short form) use it.
// No import of three or DOM.

import { type HowToPlayCard, type TableRules, TURN_RULE_LINES } from "../shared/contracts.ts";
import { formatPoints, SHOT_NAMES } from "./callouts.ts";
import { LIMITS } from "./rules/constants.ts";
import { BOSS_DAMAGE, BOSS_NAMES, BOSS_SLAM_SHIELD_MS, BOSS_TELEGRAPH_MS } from "./run/boss.ts";
import { AUTO_PICK_MS, DRAFT_SIZE } from "./run/draft.ts";
import { PERKS, RUN_LIMITS } from "./run/perks.ts";
import { STAGE_COUNT, STAGES } from "./run/stages.ts";
import {
  CHAIN_MAX_POINTS,
  CHAIN_STEP_POINTS,
  FRENZY_HITS,
  FRENZY_WINDOW_MS,
  HURRY_END_VALUE,
  HURRY_START_VALUE,
} from "./run/timed-events.ts";
import type { PerkDef, StageDef } from "./run/types.ts";

/** The title of the card. It has the same text as the card of the Worker (`src/worker/howto.ts`). */
export const HOW_TO_TITLE = "HOW TO PLAY - SigmaShake Pinball";

/** The control lines of the card. */
export const HOW_TO_CONTROLS: readonly string[] = [
  "Flippers: Left Shift / Right Shift, or Z and / keys, or tap the left and right half of the screen.",
  "Launch: hold Space or Down Arrow to charge, release to launch. Hit the lit top lane for the skill shot.",
  "Nudge: X key, or a two-finger tap. Two warnings, then tilt.",
];

/** The goal lines of the long form. */
export const HOW_TO_GOALS: readonly string[] = [
  "Goal: light D-E-N-Y to guard the drains. Drop the 3 TOKENS targets, then shoot the saucer to start a mode.",
  "Play 4 modes and SigmaBot Swarm multiball to start ASK MODE: PROD BOT.",
  `The perk draft offers ${DRAFT_SIZE} perks. They are drawn evenly from the perks that you can still take. Every perk shows its effect. There are no hidden odds. Money never buys a perk, an unlock or score.`,
  "Free play is open to all. A donation gives a featured turn on stream. Nothing else.",
];

/** Long form: the card on the site and the full overlay panel. */
export const HOW_TO_LONG: readonly string[] = [HOW_TO_TITLE, ...HOW_TO_CONTROLS, ...HOW_TO_GOALS];

/** Short form: strip zone A5 and card row B4. */
export const HOW_TO_SHORT: readonly string[] = [
  "FLIPPERS: L/R SHIFT OR TAP L/R",
  "LAUNCH: HOLD SPACE, RELEASE",
  "GOAL: LIGHT D-E-N-Y TO GUARD",
  "TOKENS + SAUCER = START A MODE",
];

/** Build the how-to-play card of the contracts from the table rules. */
export function buildHowToCard(rules: TableRules): HowToPlayCard {
  return {
    title: HOW_TO_TITLE,
    controls: HOW_TO_CONTROLS,
    turn_rule: TURN_RULE_LINES,
    modes: rules.modes,
  };
}

/** One line for each published score value, for the site card. Nothing is hidden. */
export function scoreLines(rules: TableRules): readonly string[] {
  return rules.scoreTable.map((s) => `${s.label}: ${formatPoints(s.points)}`);
}

/** The goal of a stage, in words. */
function goalWords(goal: StageDef["goal"]): string {
  const t = goal.target;
  switch (goal.kind) {
    case "score":
      return `score ${formatPoints(t)} points`;
    case "modes":
      return `finish ${t} timed ${t === 1 ? "mode" : "modes"}`;
    case "multiball":
      return "start a multiball";
    case "lit_shots":
      return `make ${t} lit shots`;
    case "jackpots":
      return `collect ${t} ${t === 1 ? "jackpot" : "jackpots"}`;
  }
}

/** One line for each stage: the goal, the lit shots, the hurry-up ramp and the twist. */
export function stageHowToLines(): readonly string[] {
  return STAGES.map((s) => {
    const lit = s.litShots.map((id) => SHOT_NAMES[id]).join(", ");
    return (
      `Stage ${s.number} of ${STAGE_COUNT}, ${s.label}: ${goalWords(s.goal)}. ` +
      `Lit shots: ${lit}. Hurry-up ramp: ${SHOT_NAMES[s.hurryShot]}. ${s.twistText}`
    );
  });
}

/** One line for a perk: the label, the effect and the most copies that you can hold. */
function perkHowToLine(p: PerkDef): string {
  return `${p.label}: ${p.text} Up to ${p.maxStacks} ${p.maxStacks === 1 ? "copy" : "copies"}.`;
}

/** One line for each perk of the catalog, in draft order. */
export function perkHowToLines(): readonly string[] {
  return PERKS.map(perkHowToLine);
}

const SECONDS = 1000;

/** The lines of the boss fight. */
function bossRuleLines(): readonly string[] {
  const d = BOSS_DAMAGE;
  return [
    `Boss fight: a SigmaBot boss waits at the end of each stage. The bosses are ${BOSS_NAMES.join(", ")}.`,
    `Hit the DENY targets, the pop bumpers, the ramps and the orbits to damage the boss: ${d.target} for a target, ${d.bumper} for a bumper, ${d.ramp} for a ramp or an orbit.`,
    `Shield slam: the boss shows a warning for ${BOSS_TELEGRAPH_MS / SECONDS} s. Then the shield blocks all damage for ${BOSS_SLAM_SHIELD_MS / SECONDS} s. A slam never drains a ball and never takes score.`,
  ];
}

/** The lines of the lock multiball, the bank combo, the victory multiball and the Spare Bot card. */
function multiballRuleLines(): readonly string[] {
  return [
    `Lock multiball: shoot the ${SHOT_NAMES.rampLeft} ${LIMITS.rampShotsFirstLock} times to light LOCK. Lock ${LIMITS.locksForMultiball} balls at the saucer to start SigmaBot Swarm.`,
    `Bank combo: clear the TOKENS bank and the DENY bank on one ball to add ${LIMITS.bankComboBalls} ball. It pays one time for each ball.`,
    `Victory multiball: beat a boss to add ${LIMITS.victoryMultiballBalls} balls to play.`,
    `Spare Bot card: a perk that adds 1 ball to play. Up to ${LIMITS.maxLiveBalls} balls play at once.`,
  ];
}

/** The lines of the draft rules, the timed events, the pause rule, the boss and the multiballs. */
export function runRuleLines(): readonly string[] {
  return [
    `After each stage you pick 1 of ${DRAFT_SIZE} perks. The draft draws evenly from the perks that you can still take.`,
    "Every perk shows its effect. There are no hidden odds. A perk never changes physics.",
    `Featured turn: the draft picks the first card after ${AUTO_PICK_MS / SECONDS} s. Free play never picks for you.`,
    `Hurry-up: a full TOKENS bank starts it. It lasts ${RUN_LIMITS.HURRY_MS / SECONDS} s and falls from ${formatPoints(HURRY_START_VALUE)} to ${formatPoints(HURRY_END_VALUE)}. Shoot the hurry-up ramp to collect.`,
    `Frenzy: ${FRENZY_HITS} bumper hits in ${FRENZY_WINDOW_MS / SECONDS} s double the switch scores for ${RUN_LIMITS.FRENZY_MS / SECONDS} s.`,
    `Combo chain: each combo shot in a row adds ${formatPoints(CHAIN_STEP_POINTS)} to the award, up to ${formatPoints(CHAIN_MAX_POINTS)}.`,
    `Extra ball: a stage clear can light it. Max ${RUN_LIMITS.MAX_EXTRA_BALLS_STAGE} for each stage and ${RUN_LIMITS.MAX_EXTRA_BALLS_GAME} for each game. Shoot the right orbit to collect.`,
    "Timers stop while the game is paused.",
    ...bossRuleLines(),
    ...multiballRuleLines(),
  ];
}

/** Long form of the run: the stages, the perks and the rules. */
export const HOW_TO_RUN: readonly string[] = [
  ...runRuleLines(),
  ...stageHowToLines(),
  ...perkHowToLines(),
];
